import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, Eye, Trash2, ArrowRight, FileText,
    MapPin, Phone, Calendar, DollarSign,
    LayoutGrid, List
} from 'lucide-react';
import { API_BASE_URL, PREVIEW_OFFLINE } from '../../config';
import useAppStore from '../../store/useAppStore';
import { Pagination } from '../../components/common/Pagination';

interface SavedQuotationSummary {
    id: string;
    quote_number: string;
    quote_date: string;
    customer_name: string;
    location: string | null;
    mobile: string | null;
    created_at: string;
    total_with_tax: number;
}

type ViewMode = 'grid' | 'list';

const ITEMS_PER_PAGE = 10;

const ModernAdminQuotationsScreen: React.FC = () => {
    const navigate = useNavigate();
    const { openConfirmation } = useAppStore();

    const [items, setItems] = useState<SavedQuotationSummary[]>([]);
    const [search, setSearch] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        return (localStorage.getItem('quotations_view_mode') as ViewMode) || 'grid';
    });

    const toggleViewMode = (mode: ViewMode) => {
        setViewMode(mode);
        localStorage.setItem('quotations_view_mode', mode);
    };

    useEffect(() => {
        const fetchQuotations = async () => {
            try {
                setLoading(true);
                if (PREVIEW_OFFLINE) return;
                const res = await fetch(`${API_BASE_URL}/quotations?limit=60`);
                if (!res.ok) return;
                const data = await res.json();
                if (Array.isArray(data)) setItems(data as SavedQuotationSummary[]);
                else if (Array.isArray((data as any).items)) setItems(((data as any).items) as SavedQuotationSummary[]);
            } catch (error) {
                console.error("Failed to fetch quotations", error);
            } finally {
                setLoading(false);
            }
        };
        fetchQuotations();
    }, []);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return items.filter(it => {
            const matchSearch = q === '' || [it.customer_name, it.quote_number, it.location || '', it.mobile || ''].some(v => String(v || '').toLowerCase().includes(q));
            const matchDate = dateFilter === '' || new Date(it.quote_date).toISOString().slice(0, 10) === dateFilter;
            return matchSearch && matchDate;
        });
    }, [items, search, dateFilter]);

    const paginated = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filtered.slice(start, start + ITEMS_PER_PAGE);
    }, [filtered, currentPage]);

    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;

    const deleteQuotation = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        openConfirmation('هل أنت متأكد من حذف هذا العرض؟ لا يمكن التراجع عن هذا الإجراء.', async () => {
            try {
                if (PREVIEW_OFFLINE) return;
                const res = await fetch(`${API_BASE_URL}/quotations/${id}`, { method: 'DELETE' });
                if (res.status === 204) {
                    setItems(prev => prev.filter(x => String(x.id) !== String(id)));
                }
            } catch { }
        });
    };

    return (
        <div className="space-y-8 animate-contentFadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text flex items-center gap-3">
                        <FileText className="text-cyan-400" size={32} />
                        عروض السعر
                    </h1>
                    <p className="text-slate-400 mt-1">إدارة واستعراض عروض الأسعار المحفوظة</p>
                </div>
                <button
                    onClick={() => navigate('/')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 transition-all hover:text-white"
                >
                    <ArrowRight size={18} />
                    <span>رجوع للرئيسية</span>
                </button>
            </div>

            {/* Filters + View Toggle */}
            <div className="glass-card p-4 space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            placeholder="بحث باسم العميل، رقم العرض، أو الجوال..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-3 pr-10 pl-4 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                        />
                    </div>
                    <div className="relative w-full md:w-48">
                        <input
                            type="date"
                            value={dateFilter}
                            onChange={e => setDateFilter(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-slate-200 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all [color-scheme:dark]"
                        />
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex items-center gap-1 bg-black/30 border border-white/10 rounded-xl p-1 self-center">
                        <button
                            onClick={() => toggleViewMode('grid')}
                            title="عرض مربعات"
                            className={`p-2 rounded-lg transition-all duration-200 ${
                                viewMode === 'grid'
                                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                    : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                            }`}
                        >
                            <LayoutGrid size={18} />
                        </button>
                        <button
                            onClick={() => toggleViewMode('list')}
                            title="عرض قائمة"
                            className={`p-2 rounded-lg transition-all duration-200 ${
                                viewMode === 'list'
                                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                    : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                            }`}
                        >
                            <List size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="glass-card p-6 h-48 animate-pulse">
                            <div className="h-6 w-2/3 bg-white/5 rounded mb-4"></div>
                            <div className="h-4 w-1/2 bg-white/5 rounded mb-2"></div>
                            <div className="h-4 w-1/3 bg-white/5 rounded"></div>
                        </div>
                    ))}
                </div>
            ) : paginated.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center glass-panel rounded-2xl border border-white/5">
                    <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
                        <FileText size={40} className="text-slate-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-300">لا توجد عروض أسعار</h3>
                    <p className="text-slate-500 mt-2">لم يتم العثور على عروض أسعار تطابق معايير البحث.</p>
                    <button
                        onClick={() => { setSearch(''); setDateFilter(''); }}
                        className="mt-6 text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                        مسح الفلاتر
                    </button>
                </div>
            ) : viewMode === 'grid' ? (
                /* ── Grid View ── */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginated.map((it, idx) => (
                        <div
                            key={it.id}
                            onClick={() => navigate(`/quotations/${it.id}`)}
                            className="glass-card p-0 cursor-pointer group hover:-translate-y-1 transition-all duration-300 animate-fadeInUp"
                            style={{ animationDelay: `${idx * 50}ms` }}
                        >
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 group-hover:border-cyan-500/40 transition-colors">
                                        <FileText className="text-cyan-400" size={24} />
                                    </div>
                                    <div className="text-right">
                                        <span className="block text-xs text-slate-500 font-mono mb-1">{it.quote_number}</span>
                                        <span className="inline-flex items-center gap-1 text-xs text-slate-400 bg-white/5 px-2 py-1 rounded-full border border-white/5">
                                            <Calendar size={10} />
                                            {new Date(it.quote_date || it.created_at).toLocaleDateString('ar-SA')}
                                        </span>
                                    </div>
                                </div>

                                <h3 className="text-lg font-bold text-white mb-1 line-clamp-1 group-hover:text-cyan-400 transition-colors">
                                    {it.customer_name}
                                </h3>

                                <div className="space-y-2 mt-4">
                                    {it.location && (
                                        <div className="flex items-center gap-2 text-sm text-slate-400">
                                            <MapPin size={14} className="text-slate-500" />
                                            <span className="truncate">{it.location}</span>
                                        </div>
                                    )}
                                    {it.mobile && (
                                        <div className="flex items-center gap-2 text-sm text-slate-400">
                                            <Phone size={14} className="text-slate-500" />
                                            <span className="truncate" dir="ltr">{it.mobile}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t border-white/5 bg-white/5 flex justify-between items-center">
                                <div className="flex items-center gap-1 text-emerald-400 font-bold">
                                    <DollarSign size={16} />
                                    <span>{Number(it.total_with_tax || 0).toLocaleString('ar-EG')} ر.س</span>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); navigate(`/quotations/${it.id}`); }}
                                        className="p-2 rounded-lg hover:bg-cyan-500/20 text-slate-400 hover:text-cyan-400 transition-colors"
                                        title="عرض"
                                    >
                                        <Eye size={18} />
                                    </button>
                                    <button
                                        onClick={(e) => deleteQuotation(it.id, e)}
                                        className="p-2 rounded-lg hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 transition-colors"
                                        title="حذف"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                /* ── List View ── */
                <div className="glass-card p-0 overflow-hidden">
                    {/* Table Header */}
                    <div className="hidden md:grid grid-cols-[2fr_1.5fr_1.2fr_1.2fr_1fr_auto] gap-4 px-5 py-3 bg-white/5 border-b border-white/10 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <span>العميل</span>
                        <span>رقم العرض</span>
                        <span>الموقع</span>
                        <span>الجوال</span>
                        <span>التاريخ</span>
                        <span>الإجمالي</span>
                    </div>

                    {/* Rows */}
                    <div className="divide-y divide-white/5">
                        {paginated.map((it, idx) => (
                            <div
                                key={it.id}
                                onClick={() => navigate(`/quotations/${it.id}`)}
                                className="grid grid-cols-1 md:grid-cols-[2fr_1.5fr_1.2fr_1.2fr_1fr_auto] gap-2 md:gap-4 items-center px-5 py-4 cursor-pointer group hover:bg-white/5 transition-all duration-200 animate-fadeInUp"
                                style={{ animationDelay: `${idx * 30}ms` }}
                            >
                                {/* Customer Name */}
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500/15 to-blue-500/15 border border-cyan-500/20 flex items-center justify-center flex-shrink-0 group-hover:border-cyan-500/40 transition-colors">
                                        <FileText size={16} className="text-cyan-400" />
                                    </div>
                                    <span className="font-semibold text-white group-hover:text-cyan-400 transition-colors truncate">
                                        {it.customer_name}
                                    </span>
                                </div>

                                {/* Quote Number */}
                                <span className="font-mono text-sm text-slate-400 flex items-center gap-1">
                                    <span className="text-slate-600 md:hidden text-xs">رقم العرض: </span>
                                    {it.quote_number}
                                </span>

                                {/* Location */}
                                <div className="flex items-center gap-1 text-sm text-slate-400 truncate">
                                    {it.location
                                        ? <><MapPin size={13} className="text-slate-600 flex-shrink-0" /><span className="truncate">{it.location}</span></>
                                        : <span className="text-slate-600">—</span>
                                    }
                                </div>

                                {/* Mobile */}
                                <div className="flex items-center gap-1 text-sm text-slate-400" dir="ltr">
                                    {it.mobile
                                        ? <><Phone size={13} className="text-slate-600 flex-shrink-0" /><span>{it.mobile}</span></>
                                        : <span className="text-slate-600">—</span>
                                    }
                                </div>

                                {/* Date */}
                                <div className="flex items-center gap-1 text-sm text-slate-400">
                                    <Calendar size={13} className="text-slate-600 flex-shrink-0" />
                                    <span>{new Date(it.quote_date || it.created_at).toLocaleDateString('ar-SA')}</span>
                                </div>

                                {/* Total + Actions */}
                                <div className="flex items-center gap-3 justify-between md:justify-end">
                                    <span className="font-bold text-emerald-400 text-sm whitespace-nowrap">
                                        {Number(it.total_with_tax || 0).toLocaleString('ar-EG')} ر.س
                                    </span>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); navigate(`/quotations/${it.id}`); }}
                                            className="p-1.5 rounded-lg hover:bg-cyan-500/20 text-slate-500 hover:text-cyan-400 transition-colors"
                                            title="عرض"
                                        >
                                            <Eye size={16} />
                                        </button>
                                        <button
                                            onClick={(e) => deleteQuotation(it.id, e)}
                                            className="p-1.5 rounded-lg hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition-colors"
                                            title="حذف"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer summary */}
                    <div className="px-5 py-3 bg-white/5 border-t border-white/10 flex justify-between items-center text-xs text-slate-500">
                        <span>إجمالي النتائج: <span className="text-cyan-400 font-semibold">{filtered.length}</span> عرض</span>
                        <span>
                            المجموع الكلي:&nbsp;
                            <span className="text-emerald-400 font-semibold">
                                {paginated.reduce((sum, it) => sum + Number(it.total_with_tax || 0), 0).toLocaleString('ar-EG')} ر.س
                            </span>
                        </span>
                    </div>
                </div>
            )}

            {totalPages > 1 && (
                <div className="flex justify-center mt-8">
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
            )}
        </div>
    );
};

export default ModernAdminQuotationsScreen;
